import React, { useEffect } from "react";
import "./Workspace.css";

export default function Network() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Resolving Network Performance Issues When Your Local Connection Is Healthy | SignalTuner";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className="workspace-page">
      <article className="workspace-article" aria-labelledby="network-title">
        <h1 id="network-title">Resolving Network Performance Issues When Your Local Connection Is Healthy</h1>

        <p>A strong connection between your computer and its network access point does not necessarily mean that your internet connection is performing well.</p>

        <p>Your computer may have an excellent Ethernet or Wi-Fi connection to the router while still experiencing problems farther along the network path. These problems can cause frozen video, distorted audio, delayed conversations, reduced video quality, and intermittent disconnections in services such as Microsoft Teams, Zoom, Webex, and Google Meet.</p>

        <p>If the connection between the device and local network access point is healthy, but testing shows <strong>slow download speeds, slow upload speeds, high latency, packet loss, or high jitter</strong>, troubleshooting should move beyond the local Wi-Fi or Ethernet connection.</p>

        <p>Potential problem areas include:</p>

        <ul>
          <li>Other devices consuming internet bandwidth</li>
          <li>Large uploads or downloads</li>
          <li>Router or modem performance</li>
          <li>Internet service congestion</li>
          <li>An overloaded upstream connection</li>
          <li>VPNs</li>
          <li>Poor routing between the ISP and meeting service</li>
          <li>Insufficient internet-service capacity</li>
          <li>Bufferbloat</li>
          <li>Problems with the modem, ONT, coaxial connection, or ISP line</li>
          <li>Problems outside the user's network entirely</li>
        </ul>

        <p>For video meetings, raw download speed is only one part of connection quality. <strong>Upload capacity, latency, packet loss, and jitter are often equally—or more—important.</strong></p>

        <p>As a reference point, Zoom currently recommends latency of approximately <strong>150 ms or less</strong>, jitter of <strong>40 ms or less</strong>, and packet loss of <strong>2% or less</strong>. Its bandwidth requirements vary according to resolution and meeting configuration; for example, 1080p group video can require several megabits per second in both directions.</p>

        <h2>Start by Determining Whether the Problem Is Persistent or Temporary</h2>

        <p>Before changing network equipment or service plans, determine when the problem occurs.</p>

        <p>Ask:</p>

        <ul>
          <li>Does the problem occur throughout the day or only at certain times?</li>
          <li>Does it occur during every video meeting?</li>
          <li>Does it affect multiple computers or only one?</li>
          <li>Does it appear when another person starts streaming, gaming, downloading, or uploading?</li>
          <li>Does performance improve when a VPN is disconnected?</li>
          <li>Does the problem occur on multiple meeting platforms?</li>
          <li>Does it disappear when using a mobile hotspot?</li>
          <li>Are speed and latency normal when the network is otherwise idle?</li>
        </ul>

        <p>These observations can narrow the problem considerably.</p>

        <p>For example, a connection that performs well in the morning but degrades every evening may indicate ISP or neighborhood congestion. A connection that deteriorates whenever cloud backup software runs is more likely suffering from local bandwidth saturation.</p>

        <h2>Run Tests When the Network Is Idle</h2>

        <p>Before troubleshooting individual metrics, test the internet connection while unnecessary traffic is stopped.</p>

        <p>Temporarily pause:</p>

        <ul>
          <li>Cloud backups</li>
          <li>File synchronization</li>
          <li>Large downloads</li>
          <li>Software and game updates</li>
          <li>Streaming video</li>
          <li>Security-camera cloud uploads</li>
          <li>Large file transfers</li>
          <li>Online backup services</li>
        </ul>

        <p>Then test the connection again.</p>

        <p>It can also be useful to perform the same tests while the network is under normal household or office load. A connection that tests well while idle but becomes unstable during normal usage may have adequate nominal bandwidth but poor congestion management.</p>

        <hr />

        <h2>Slow Download Speeds</h2>

        <p>Download speed measures how quickly data can travel <strong>from the internet to your device</strong>.</p>

        <p>During a video meeting, download capacity is used to receive:</p>

        <ul>
          <li>Other participants' video</li>
          <li>Other participants' audio</li>
          <li>Screen sharing</li>
          <li>Shared presentations or media</li>
          <li>Meeting-related data</li>
        </ul>

        <p>Insufficient download bandwidth can cause incoming video to lose resolution, freeze, or stop temporarily. Audio may also become distorted or intermittent.</p>

        <h3>Check Whether Other Devices Are Consuming Bandwidth</h3>

        <p>One of the most common causes of temporarily reduced download performance is competition from other devices.</p>

        <p>Examples include:</p>

        <ul>
          <li>Streaming 4K video</li>
          <li>Downloading games</li>
          <li>Operating-system updates</li>
          <li>Game-console updates</li>
          <li>Large cloud downloads</li>
          <li>Multiple simultaneous video meetings</li>
        </ul>

        <p>Pause unnecessary activity and test again.</p>

        <p>If performance improves dramatically, the problem is probably not the internet connection itself but contention for the available capacity.</p>

        <h3>Check Your Internet Plan</h3>

        <p>Compare measured performance with the service tier purchased from the ISP.</p>

        <p>Keep in mind that advertised speeds may represent maximum or expected service rates rather than a guarantee that the full rate will always be available.</p>

        <p>If a household or office regularly requires more bandwidth than the connection provides, upgrading the internet plan may be appropriate.</p>

        <p>However, upgrading from an already-fast plan will not necessarily improve latency, jitter, or packet loss. A 1 Gbps connection can still perform poorly in a meeting if packets are delayed or lost.</p>

        <h3>Restart the Internet Gateway, Modem, or ONT</h3>

        <p>A modem, router, or combined gateway can occasionally develop temporary performance problems.</p>

        <p>A normal restart can clear:</p>

        <ul>
          <li>Stalled network processes</li>
          <li>Temporary resource exhaustion</li>
          <li>Connection-state problems</li>
          <li>Some modem synchronization issues</li>
        </ul>

        <p>Avoid performing a <strong>factory reset</strong> as a routine troubleshooting measure. A factory reset erases network configuration and is considerably different from simply restarting the device.</p>

        <h3>Check the Modem or ISP Connection</h3>

        <p>If the local Ethernet or Wi-Fi connection is healthy but download speeds remain substantially below expectations across multiple devices, the problem may be between the router and ISP.</p>

        <p>Depending on the internet technology, this could involve:</p>

        <ul>
          <li>A cable modem</li>
          <li>Fiber ONT</li>
          <li>DSL modem</li>
          <li>Fixed-wireless receiver</li>
          <li>Cellular gateway</li>
          <li>ISP line or infrastructure</li>
        </ul>

        <p>Testing multiple devices can help isolate the problem.</p>

        <p>If every device connected to the network exhibits similarly poor performance, the problem is less likely to be a single computer.</p>

        <hr />

        <h2>Slow Upload Speeds</h2>

        <p>Upload speed measures how quickly data travels <strong>from your device to the internet</strong>.</p>

        <p>For video meetings, upload performance is particularly important because your computer continuously transmits:</p>

        <ul>
          <li>Your camera feed</li>
          <li>Your microphone audio</li>
          <li>Screen sharing</li>
          <li>Presentation data</li>
          <li>Meeting-control traffic</li>
        </ul>

        <p>A connection can have extremely fast download speeds while providing relatively little upload capacity.</p>

        <p>This is common with some cable and legacy broadband plans.</p>

        <h3>Check for Background Uploads</h3>

        <p>Upload saturation is especially disruptive to real-time communications.</p>

        <p>Potential sources include:</p>

        <ul>
          <li>OneDrive, Dropbox, Google Drive, or iCloud synchronization</li>
          <li>Online backup services</li>
          <li>Uploading videos or photographs</li>
          <li>Security cameras</li>
          <li>NAS cloud synchronization</li>
          <li>Large email attachments</li>
          <li>File transfers</li>
          <li>Livestreaming</li>
          <li>Peer-to-peer applications</li>
        </ul>

        <p>Pause these activities and retest.</p>

        <h3>Pay Particular Attention to Cloud Backup</h3>

        <p>Cloud backup applications can consume nearly all available upstream bandwidth if they are not configured with limits.</p>

        <p>This may create a situation where a speed test shows acceptable results when the backup is paused but video meetings perform poorly while it is active.</p>

        <p>Configure backup and synchronization applications to:</p>

        <ul>
          <li>Limit upload bandwidth</li>
          <li>Run outside working hours</li>
          <li>Pause automatically during meetings, when supported</li>
        </ul>

        <h3>Consider a Higher-Upload Internet Service</h3>

        <p>If upload capacity is consistently inadequate even when the network is idle, the internet plan itself may be the limiting factor.</p>

        <p>Fiber service often provides substantially greater upstream capacity than asymmetric cable or DSL plans.</p>

        <p>For a household containing several people participating in simultaneous video meetings, uploading files, and using cloud services, upstream capacity can become more important than additional downstream capacity.</p>

        <hr />

        <h2>High Latency</h2>

        <p><strong>Latency</strong> is the amount of time required for network traffic to travel between endpoints.</p>

        <p>For conversational applications, low latency is essential.</p>

        <p>High latency can cause:</p>

        <ul>
          <li>Noticeable pauses after someone speaks</li>
          <li>Participants talking over each other</li>
          <li>Delayed reactions</li>
          <li>Slow screen sharing</li>
          <li>Delayed control responses</li>
          <li>Audio and video synchronization problems</li>
        </ul>

        <p>Microsoft notes that excessive round-trip time can create noticeable conversational delays, while Zoom recommends latency around 150 ms or less for good meeting performance.</p>

        <h3>Eliminate Network Saturation First</h3>

        <p>A major source of high latency is a fully utilized internet connection.</p>

        <p>When the connection becomes saturated, packets wait in queues before they can be transmitted.</p>

        <p>This can cause latency to increase dramatically even though the connection itself has not failed.</p>

        <p>For example, a connection might normally exhibit:</p>

        <p><strong>20 ms latency</strong></p>

        <p>but increase to:</p>

        <p><strong>200–500 ms or more</strong></p>

        <p>while a large upload or download is consuming the connection.</p>

        <p>This phenomenon is frequently associated with <strong>bufferbloat</strong>.</p>

        <h3>Address Bufferbloat</h3>

        <p>Bufferbloat occurs when networking equipment maintains excessively large queues of packets during congestion.</p>

        <p>Instead of dropping or intelligently prioritizing traffic, the router allows packets to sit in a queue. Real-time meeting traffic then waits behind less time-sensitive traffic such as file transfers.</p>

        <p>The result may be:</p>

        <ul>
          <li>High latency</li>
          <li>High jitter</li>
          <li>Delayed audio</li>
          <li>Delayed video</li>
          <li>Poor responsiveness</li>
        </ul>

        <p>One of the most effective solutions is <strong>Smart Queue Management</strong>, commonly abbreviated <strong>SQM</strong>.</p>

        <p>Technologies such as <strong>FQ-CoDel</strong> and <strong>CAKE</strong> can control queues and prevent bulk transfers from severely affecting interactive traffic.</p>

        <p>Some consumer routers expose similar functionality under names such as:</p>

        <ul>
          <li>Smart Queue Management</li>
          <li>Adaptive QoS</li>
          <li>Traffic Prioritization</li>
          <li>Gaming QoS</li>
          <li>Media Prioritization</li>
        </ul>

        <p>Implementation varies substantially between manufacturers.</p>

        <h3>Use Quality of Service When Appropriate</h3>

        <p><strong>Quality of Service, or QoS</strong>, allows certain types of network traffic to receive priority over less time-sensitive traffic.</p>

        <p>For example, a network might prioritize real-time audio and video ahead of large file downloads.</p>

        <p>Microsoft specifically recommends QoS as a mechanism for prioritizing latency-sensitive Teams media traffic and notes that network congestion can significantly affect media quality.</p>

        <p>QoS is particularly useful when bandwidth is limited or multiple users share the connection.</p>

        <p>For home networks, intelligent queue management is often preferable to manually assigning priorities to individual devices.</p>

        <h3>Check Geographic Distance</h3>

        <p>Latency is partly constrained by physical distance.</p>

        <p>Traffic traveling hundreds or thousands of miles will inherently take longer than traffic traveling to a nearby data center.</p>

        <p>This can become relevant when:</p>

        <ul>
          <li>Using a VPN located in another region</li>
          <li>Connecting through corporate infrastructure</li>
          <li>Joining services routed through distant gateways</li>
          <li>Traveling internationally</li>
        </ul>

        <p>The objective is generally to avoid unnecessary detours rather than trying to eliminate unavoidable propagation delay.</p>

        <hr />

        <h2>Disconnect Unnecessary VPN Connections</h2>

        <p>A VPN changes the path that network traffic takes.</p>

        <p>Instead of traveling directly from the computer toward the meeting service, traffic may first travel to a VPN gateway and then return to the public internet or corporate network.</p>

        <p>This can increase:</p>

        <ul>
          <li>Latency</li>
          <li>Jitter</li>
          <li>Packet overhead</li>
          <li>Congestion</li>
        </ul>

        <p>Microsoft specifically recommends avoiding the routing of real-time communications through a VPN when it is unnecessary because VPNs can add overhead and delay.</p>

        <p>If organizational policy permits it, <strong>VPN split tunneling</strong> can allow Teams, Zoom, or other approved real-time services to access the internet directly while corporate applications continue to use the VPN.</p>

        <p>Do not disable an employer-required VPN without authorization.</p>

        <hr />

        <h2>High Packet Loss</h2>

        <p>Data traveling over an IP network is divided into packets.</p>

        <p><strong>Packet loss</strong> occurs when some of those packets never reach their destination.</p>

        <p>Ordinary file downloads can usually recover from packet loss by retransmitting missing data. Real-time audio and video cannot always wait for retransmission.</p>

        <p>Consequently, packet loss can cause:</p>

        <ul>
          <li>Robotic or distorted audio</li>
          <li>Missing portions of speech</li>
          <li>Frozen video</li>
          <li>Blocky or pixelated video</li>
          <li>Reduced video resolution</li>
          <li>Temporary video loss</li>
          <li>Meeting disconnects</li>
        </ul>

        <p>Microsoft identifies packet loss as a direct contributor to degraded voice quality and video freezes.</p>

        <p>Zoom currently lists <strong>2% or less</strong> as a typical packet-loss target for its meeting statistics.</p>

        <h3>Determine Whether Packet Loss Is Local or External</h3>

        <p>If the computer's connection to the access point has already been verified as healthy, packet loss may be occurring:</p>

        <ul>
          <li>Between the router and modem</li>
          <li>On the ISP connection</li>
          <li>Within the ISP network</li>
          <li>At an overloaded network link</li>
          <li>Along the route to the meeting service</li>
          <li>Through a VPN</li>
          <li>At an intermediary network</li>
        </ul>

        <p>Testing another device can help determine whether the problem affects the entire network.</p>

        <h3>Test With Ethernet</h3>

        <p>Even when Wi-Fi measurements appear healthy, temporarily testing the computer over Ethernet can provide a useful control test.</p>

        <p>If packet loss persists on Ethernet, attention should move farther upstream.</p>

        <p>If the loss disappears, the local wireless connection may still be contributing despite otherwise healthy signal measurements.</p>

        <h3>Restart the Modem or Gateway</h3>

        <p>Temporary modem or gateway problems can cause packet loss.</p>

        <p>Restart the networking equipment and retest.</p>

        <p>If the packet loss repeatedly returns, further investigation is preferable to repeatedly rebooting the device.</p>

        <h3>Check Physical ISP Connections</h3>

        <p>For cable internet, damaged or poorly installed coaxial connections can contribute to signal problems.</p>

        <p>Check for:</p>

        <ul>
          <li>Loose coaxial connectors</li>
          <li>Damaged cables</li>
          <li>Unnecessary splitters</li>
          <li>Poor-quality splitters</li>
          <li>Corroded exterior connections</li>
        </ul>

        <p>Fiber and DSL connections can develop their own physical-layer problems and generally require ISP diagnosis.</p>

        <h3>Contact the ISP for Persistent Packet Loss</h3>

        <p>If packet loss:</p>

        <ul>
          <li>Occurs on multiple devices</li>
          <li>Occurs over Ethernet</li>
          <li>Persists with the local network otherwise idle</li>
          <li>Continues after restarting network equipment</li>
        </ul>

        <p>the ISP may need to investigate the connection.</p>

        <p>Provide specific information where possible, including:</p>

        <ul>
          <li>Dates and times</li>
          <li>Packet-loss percentages</li>
          <li>Whether the problem is continuous or intermittent</li>
          <li>Whether multiple devices are affected</li>
          <li>Whether Ethernet testing produces the same result</li>
        </ul>

        <p>This information is considerably more actionable than simply reporting that "the internet is slow."</p>

        <hr />

        <h2>High Jitter</h2>

        <p><strong>Jitter</strong> is variation in packet arrival time.</p>

        <p>Consider two connections that both average 40 ms of latency.</p>

        <p>On the first connection, packets might arrive at:</p>

        <p>39 ms, 41 ms, 40 ms, 40 ms, 39 ms.</p>

        <p>On the second:</p>

        <p>10 ms, 85 ms, 25 ms, 100 ms, 15 ms.</p>

        <p>Although the average latency might appear similar, the second connection is far less predictable.</p>

        <p>That variation is jitter.</p>

        <p>Real-time communications depend on audio and video packets arriving at relatively consistent intervals. High jitter can therefore cause:</p>

        <ul>
          <li>Choppy audio</li>
          <li>Brief audio gaps</li>
          <li>Video stuttering</li>
          <li>Frozen frames</li>
          <li>Audio/video synchronization problems</li>
        </ul>

        <p>Microsoft defines jitter as variation in network propagation delay, while Zoom currently recommends jitter around <strong>40 ms or less</strong>.</p>

        <h3>Reduce Network Congestion</h3>

        <p>Congestion is one of the most common causes of jitter.</p>

        <p>Look for:</p>

        <ul>
          <li>Large downloads</li>
          <li>Large uploads</li>
          <li>Multiple video streams</li>
          <li>Cloud backups</li>
          <li>Game downloads</li>
          <li>Security-camera uploads</li>
          <li>Other simultaneous meetings</li>
        </ul>

        <p>Pause these activities and compare results.</p>

        <h3>Address Bufferbloat</h3>

        <p>High jitter and high latency frequently appear together when a connection becomes saturated.</p>

        <p>A router supporting effective SQM or QoS may significantly improve consistency even without increasing the internet connection's maximum speed.</p>

        <p>For video meetings, <strong>consistency is often more valuable than maximum throughput</strong>.</p>

        <h3>Check Whether Jitter Occurs Only at Certain Times</h3>

        <p>If jitter becomes significantly worse during peak evening hours or other predictable periods, upstream network congestion may be involved.</p>

        <p>Record test results at different times of day.</p>

        <p>A clear time-dependent pattern can be useful when discussing the problem with an ISP.</p>

        <hr />

        <h2>Check the Router's Internet-Facing Performance</h2>

        <p>A router can provide an excellent local Wi-Fi connection while performing poorly when routing traffic to the internet.</p>

        <p>Possible problems include:</p>

        <ul>
          <li>Insufficient router processing capability</li>
          <li>Outdated firmware</li>
          <li>Thermal problems</li>
          <li>Excessive traffic</li>
          <li>Poor QoS implementation</li>
          <li>Malfunctioning hardware</li>
        </ul>

        <p>This is particularly relevant with older routers being used with much faster internet connections than they were originally designed to handle.</p>

        <p>Restart the router, install current firmware, and determine whether performance problems affect every connected device.</p>

        <p>If possible, compare results with a computer connected directly to the ISP gateway or modem under a configuration supported by the ISP.</p>

        <hr />

        <h2>Check for Double NAT and Unnecessarily Complex Network Paths</h2>

        <p>Some networks contain several routing devices.</p>

        <p>For example:</p>

        <p><strong>ISP gateway → personal router → mesh router → computer</strong></p>

        <p>This can create unnecessary complexity and occasionally interfere with real-time applications.</p>

        <p>Common situations include:</p>

        <ul>
          <li>An ISP gateway operating as a router while another router is installed behind it</li>
          <li>Multiple independent mesh systems</li>
          <li>Old routers being used as access points but still performing routing</li>
          <li>Multiple NAT layers</li>
        </ul>

        <p>Where appropriate, configure secondary devices as access points or place the ISP gateway into bridge or passthrough mode.</p>

        <p>Only make these changes if you understand the existing network configuration, because changing routing modes can temporarily disconnect the network.</p>

        <hr />

        <h2>Test Without the VPN, Proxy, or Security Filter</h2>

        <p>Corporate security software may route network traffic through:</p>

        <ul>
          <li>VPN gateways</li>
          <li>Secure web gateways</li>
          <li>Proxies</li>
          <li>Cloud security platforms</li>
          <li>Traffic inspection systems</li>
        </ul>

        <p>These systems can increase path length or introduce congestion.</p>

        <p>If policy allows, compare performance with and without the intermediary service.</p>

        <p>If disabling it resolves the problem, the appropriate long-term solution may involve configuration changes by the organization's IT department rather than permanently bypassing security controls.</p>

        <hr />

        <h2>Use Meeting Application Statistics</h2>

        <p>Speed tests provide useful information, but they do not necessarily measure the exact network path used by a meeting.</p>

        <p>Many video meeting applications expose their own connection statistics.</p>

        <p>These can include:</p>

        <ul>
          <li>Current bitrate</li>
          <li>Latency</li>
          <li>Jitter</li>
          <li>Packet loss</li>
          <li>Resolution</li>
          <li>Frame rate</li>
        </ul>

        <p>Zoom, for example, exposes latency, jitter, packet loss, and bandwidth statistics during meetings. Microsoft Teams provides real-time telemetry and administrative Call Quality tools for diagnosing meeting-performance problems.</p>

        <p>These measurements can be especially useful because they reflect the actual media session rather than a generic connection to a nearby speed-test server.</p>

        <hr />

        <h2>Remember That Speed Tests Do Not Tell the Entire Story</h2>

        <p>A conventional speed test may report:</p>

        <p><strong>500 Mbps download / 30 Mbps upload</strong></p>

        <p>and still produce a poor video-meeting experience.</p>

        <p>The connection could simultaneously exhibit:</p>

        <ul>
          <li>High latency</li>
          <li>Packet loss</li>
          <li>High jitter</li>
          <li>Severe latency under load</li>
        </ul>

        <p>Conversely, a stable 50 Mbps connection with low latency and negligible packet loss can provide an excellent meeting experience.</p>

        <p>When optimizing for video conferencing, evaluate:</p>

        <p><strong>Bandwidth + latency + packet loss + jitter + stability under load</strong></p>

        <p>rather than bandwidth alone.</p>

        <hr />

        <h2>Try a Mobile Hotspot as a Diagnostic Test</h2>

        <p>A smartphone or dedicated cellular hotspot can provide an entirely separate internet path.</p>

        <p>Temporarily joining a meeting through the hotspot can therefore be a useful diagnostic test.</p>

        <p>If the meeting performs normally over cellular service while the primary connection performs poorly, the problem is probably associated with:</p>

        <ul>
          <li>The primary router</li>
          <li>Modem or ONT</li>
          <li>ISP connection</li>
          <li>ISP routing</li>
        </ul>

        <p>rather than the computer or meeting application.</p>

        <p>A hotspot can also provide a useful temporary backup during an important meeting, although cellular performance can itself vary substantially.</p>

        <hr />

        <h2>When to Contact the Internet Service Provider</h2>

        <p>Contact the ISP when the problem persists across multiple devices and remains present when:</p>

        <ul>
          <li>The network is otherwise idle</li>
          <li>A computer is connected by Ethernet</li>
          <li>The router and modem have been restarted</li>
          <li>VPN software is not responsible</li>
          <li>Local Wi-Fi quality has been verified</li>
          <li>Background traffic has been eliminated</li>
        </ul>

        <p>Useful information to provide includes:</p>

        <ul>
          <li>Expected service speed</li>
          <li>Measured download and upload speeds</li>
          <li>Latency</li>
          <li>Packet loss</li>
          <li>Jitter</li>
          <li>Time of day</li>
          <li>Whether the problem is intermittent</li>
          <li>Whether Ethernet and Wi-Fi show the same behavior</li>
          <li>Whether multiple devices are affected</li>
        </ul>

        <p>Ask the provider to check the line or connection quality, not only whether the service is technically online.</p>

        <hr />

        <h2>Prioritizing Improvements for Video Meetings</h2>

        <p>When optimizing specifically for video meetings, address problems in approximately this order:</p>

        <ol>
          <li><strong>Packet loss</strong> — Lost real-time audio and video data can immediately affect call quality.</li>
          <li><strong>High jitter</strong> — Inconsistent delivery causes choppy and unstable media.</li>
          <li><strong>High latency</strong> — Excessive delay makes natural conversation difficult.</li>
          <li><strong>Insufficient upload bandwidth</strong> — Your microphone, camera, and shared content depend on upstream capacity.</li>
          <li><strong>Insufficient download bandwidth</strong> — Incoming participant video and shared content depend on downstream capacity.</li>
        </ol>

        <p>This does not mean that one metric always matters more than another. Severe deficiencies in any category can make a meeting unusable.</p>

        <hr />

        <h2>A Practical Troubleshooting Sequence</h2>

        <p>If your connection to the local network access point is healthy but meeting performance remains poor, use the following process:</p>

        <ol>
          <li>Pause large uploads, downloads, cloud synchronization, and streaming.</li>
          <li>Run download, upload, latency, packet-loss, and jitter tests again.</li>
          <li>Check the meeting application's own network statistics.</li>
          <li>Disconnect unnecessary VPN connections when permitted.</li>
          <li>Restart the router and modem or ONT.</li>
          <li>Update router firmware.</li>
          <li>Test using Ethernet.</li>
          <li>Test another computer on the same network.</li>
          <li>Test using a cellular hotspot or another internet connection.</li>
          <li>Check for latency increases while the internet connection is under load.</li>
          <li>Enable appropriate SQM or QoS functionality if congestion is the problem.</li>
          <li>Determine whether the internet service provides sufficient upstream and downstream capacity.</li>
          <li>Record performance at different times of day.</li>
          <li>Contact the ISP if packet loss, latency, jitter, or poor throughput persists across multiple devices.</li>
        </ol>

        <h3>The Goal Is Consistency, Not Just Speed</h3>

        <p>Video conferencing is a real-time application.</p>

        <p>A file download can tolerate a temporary slowdown and simply take longer to finish. A live conversation cannot.</p>

        <p>For this reason, the best connection for a video meeting is not necessarily the connection with the highest speed-test result. It is the connection that reliably delivers packets with:</p>

        <ul>
          <li>Sufficient download bandwidth</li>
          <li>Sufficient upload bandwidth</li>
          <li>Low latency</li>
          <li>Minimal packet loss</li>
          <li>Low jitter</li>
          <li>Consistent performance while the network is being used</li>
        </ul>

        <p>Once the device-to-access-point connection has been verified as healthy, troubleshooting should therefore focus on <strong>network congestion, WAN performance, router queue management, the modem or ONT, VPN routing, and the ISP path to the conferencing service</strong>.</p>
      </article>
    </main>
  );
}
